/**
 * Multi-Zone Rendering Utilities
 *
 * Handles rendering multiple zones from a single mask image
 * Each zone is extracted by color and used to mask its corresponding user image
 */

import { loadImage, setupCanvas, calculateImageScaling, logError } from './canvasRenderer';
import { loadPNGMask, calculatePNGMaskPositioning, applyPNGMaskClipping } from './pngProcessor';
import { extractColorZone, dataUrlToBlobUrl } from './zoneMaskExtractor';
import type { ZoneConfig, ZoneImageState, StepImageDimensions } from '../types';

export interface MultiZoneRenderOptions {
  zones: ZoneConfig[];
  zoneImages: Map<number, ZoneImageState>;
  stepImageDimensions: StepImageDimensions | null;
  uploadType: 'photo' | 'engraving';
  maskSvgBlob: Blob | null;
  maskImageConfig: { url: string; width: number; height: number };
  positioning?: { x: number; y: number; scale: number; rotation: number };
  showStarMapSlot?: boolean;
  actualWidth: number;
  actualHeight: number;
}

/**
 * Render a single zone image with its color-extracted mask
 */
async function renderZoneImage(
  ctx: CanvasRenderingContext2D,
  zoneImage: ZoneImageState,
  zoneConfig: ZoneConfig,
  maskImage: HTMLImageElement,
  maskPositioning: ReturnType<typeof calculatePNGMaskPositioning>,
  actualWidth: number,
  actualHeight: number,
  uploadType: 'photo' | 'engraving',
  showStarMapSlot: boolean
): Promise<void> {
  // Extract the zone mask (color-based) from the main mask image
  // Use a higher tolerance (30) to account for anti-aliasing and color variations
  const zoneMaskDataUrl = await extractColorZone(maskImage, zoneConfig.colorHex, 30);
  const zoneMaskBlobUrl = await dataUrlToBlobUrl(zoneMaskDataUrl);

  // Load the extracted zone mask as an image
  const zoneMaskImage = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load zone mask for ${zoneConfig.zoneLabel}`));
    img.src = zoneMaskBlobUrl;
  });

  // Load the user's image for this zone
  const userImage = await loadImage({
    src: zoneImage.processedSrc || zoneImage.blobUrl
  });

  // Calculate image scaling
  const { scaledWidth, scaledHeight } = calculateImageScaling({
    image: userImage,
    canvasWidth: actualWidth,
    canvasHeight: actualHeight
  });

  // Create a temporary canvas to render this zone in isolation
  // This prevents the mask from clipping other zones that are already rendered
  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = actualWidth;
  tempCanvas.height = actualHeight;
  const tempCtx = tempCanvas.getContext('2d');

  if (!tempCtx) {
    throw new Error('Could not create temporary canvas context for zone rendering');
  }

  // Initialize temp canvas with transparent background
  tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);

  // Apply user transformations for this zone on the temp canvas
  tempCtx.save();
  // Move to the mask center position first
  tempCtx.translate(maskPositioning.maskCenterX, maskPositioning.maskCenterY);
  // Then apply user transformations
  tempCtx.rotate((zoneImage.rotation * Math.PI) / 180);
  tempCtx.scale(zoneImage.scale * (zoneImage.flipX ? -1 : 1), zoneImage.scale);
  tempCtx.translate(zoneImage.offsetX, zoneImage.offsetY);

  // Apply engraving filters if needed
  const isStarMap = zoneImage.file.name === 'star-map.png' || zoneImage.file.name === 'location-map.png' || showStarMapSlot;
  if (uploadType === 'engraving' || isStarMap) {
    if (isStarMap) {
      tempCtx.globalCompositeOperation = 'multiply';
    } else {
      tempCtx.filter = 'grayscale(100%) contrast(300%)';
      tempCtx.globalCompositeOperation = 'multiply';
    }
  }

  // Draw the user image on the temp canvas
  tempCtx.drawImage(userImage, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);

  // Restore user transformations
  tempCtx.restore();

  // Apply the zone-specific mask to clip the user's image on the temp canvas
  applyPNGMaskClipping({
    ctx: tempCtx,
    maskImage: zoneMaskImage,
    positioning: maskPositioning
  });

  // Now composite the masked zone onto the main canvas using source-over
  // This preserves all previously rendered zones
  ctx.save();
  ctx.globalCompositeOperation = 'source-over';
  ctx.drawImage(tempCanvas, 0, 0);
  ctx.restore();

  // Clean up the blob URL
  URL.revokeObjectURL(zoneMaskBlobUrl);
}

/**
 * Render all zones onto a canvas
 * Each zone is rendered with its color-extracted mask
 */
export async function renderAllZones(ctx: CanvasRenderingContext2D, options: MultiZoneRenderOptions): Promise<void> {
  const {
    zones,
    zoneImages,
    maskSvgBlob,
    maskImageConfig,
    positioning,
    actualWidth,
    actualHeight,
    uploadType,
    showStarMapSlot = false
  } = options;

  if (!maskSvgBlob || zones.length === 0) {
    return;
  }

  try {
    // Load the main mask image once
    const maskImage = await loadPNGMask({ maskSvgBlob });

    // Calculate mask positioning (same for all zones since they share the same mask image)
    const maskPositioning = calculatePNGMaskPositioning({
      containerWidth: actualWidth,
      containerHeight: actualHeight,
      positioning
    });

    // Render each zone in order
    for (const zone of zones) {
      const zoneImage = zoneImages.get(zone.index);
      if (!zoneImage) {
        // Skip zones without images
        continue;
      }

      await renderZoneImage(
        ctx,
        zoneImage,
        zone,
        maskImage,
        maskPositioning,
        actualWidth,
        actualHeight,
        uploadType,
        showStarMapSlot
      );
    }

    // Apply multiply blend mode to the entire result if engraving
    if (uploadType === 'engraving' || showStarMapSlot) {
      ctx.save();
      ctx.globalCompositeOperation = 'multiply';
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.restore();
    }

    // Ensure white background is maintained in areas outside all masks
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.restore();
  } catch (error) {
    logError('Error rendering all zones', error, {
      zoneCount: zones.length,
      hasMask: !!maskSvgBlob
    });
    throw error;
  }
}
